import {
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { noDataAvailable } from '../../../components/NoDataAvailable';
import { PartnerId } from '../../../components/Partner/PartnerId';
import ICategory from '../../../models/Category.model';
import {
  categoryTableColumnNames,
  VALID_CATEGORY_TYPES,
} from './Category.config';

interface ICategoriesTableProps {
  categories: ICategory[];
  onChangeActive: (
    e: React.ChangeEvent<HTMLInputElement>,
    category: ICategory,
  ) => void;
  onDeleteCategory: (category: ICategory) => void;
  onEditCategory: (category: ICategory) => void;
  /** Callback fired after categories are reordered via drag-and-drop */
  onReorderCategories?: (updated: ICategory[]) => void;
}

const CategoriesTable = (props: ICategoriesTableProps) => {
  const {
    categories,
    onChangeActive,
    onEditCategory,
    onDeleteCategory,
    onReorderCategories,
  } = props;

  // local draggable state
  const [draggableCategories, setDraggableCategories] = useState<ICategory[]>(
    categories,
  );

  // sync with props
  useEffect(() => {
    setDraggableCategories(categories);
  }, [categories]);

  // helper to reorder & rewrite priority
  const reorder = (list: ICategory[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    // rewrite priorities (1-based)
    result.forEach((c, idx) => (c.priority = idx + 1));
    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const newOrder = reorder(
      draggableCategories,
      result.source.index,
      result.destination.index,
    );
    setDraggableCategories(newOrder);
    // Notify parent so it can persist the new priorities
    if (onReorderCategories) {
      onReorderCategories(newOrder);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Table className="categories__table">
        <Droppable droppableId="categories-droppable" direction="vertical">
          {provided => (
            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
              <TableRow classes={{ root: 'categories__table-header' }}>
                {categoryTableColumnNames.map((column: any, i: number) => (
                  <TableCell
                    key={i}
                    align="left"
                    size={column.size}
                    variant="head"
                    classes={{ root: 'categories__table-cell' }}
                  >
                    <p>{column.name}</p>
                  </TableCell>
                ))}
              </TableRow>
              {draggableCategories.length < 1
                ? noDataAvailable(5)
                : draggableCategories.map(
                    (category: ICategory, index: number) => (
                      <Draggable
                        key={category.id}
                        draggableId={category.id}
                        index={index}
                      >
                        {providedRow => (
                          <TableRow
                            ref={providedRow.innerRef}
                            hover={true}
                            {...providedRow.draggableProps}
                            {...providedRow.dragHandleProps}
                          >
                            <TableCell
                              key={`category ${category.id}_name`}
                              align="left"
                              size="medium"
                              variant="body"
                              classes={{ root: 'categories__table-cell' }}
                            >
                              <DragIndicatorIcon
                                fontSize="small"
                                className="drag-handle-icon"
                                style={{
                                  marginRight: 4,
                                  verticalAlign: 'middle',
                                }}
                              />
                              <p>{category.name}</p>
                              <PartnerId
                                id={category.partnerCategoryId}
                                partner={category.partner}
                              />
                            </TableCell>
                            <TableCell
                              key={`category ${category.id}_active`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'categories__table-cell' }}
                            >
                              {get(category, ['type']) !==
                                VALID_CATEGORY_TYPES.OPTIONS && (
                                <Switch
                                  classes={{
                                    track: 'custom-colored-switch-track',
                                  }}
                                  checked={category.active}
                                  color="primary"
                                  name="active"
                                  onChange={e => onChangeActive(e, category)}
                                  size="medium"
                                  value={category.active}
                                />
                              )}
                            </TableCell>
                            <TableCell
                              key={`category ${category.id}_priority`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'categories__table-cell' }}
                            >
                              <p>{category.priority}</p>
                            </TableCell>
                            <TableCell
                              key={`category ${category.id}_type`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'categories__table-cell' }}
                            >
                              <p>{category.type}</p>
                            </TableCell>
                            <TableCell
                              key={`category ${category.id}_action`}
                              align="left"
                              size="medium"
                              variant="body"
                              classes={{ root: 'categories__table-cell' }}
                            >
                              <Button
                                size="small"
                                classes={{ root: 'categories__action-buttons' }}
                                onClick={() => onEditCategory(category)}
                              >
                                <div className="icon-title-wrapper">
                                  <CreateOutlinedIcon />
                                  <span className="icon-title">Edit</span>
                                </div>
                              </Button>
                              <Button
                                size="small"
                                classes={{ root: 'categories__action-buttons' }}
                                onClick={() => onDeleteCategory(category)}
                              >
                                <div className="icon-title-wrapper">
                                  <DeleteOutlinedIcon />
                                  <span className="icon-title">Delete</span>
                                </div>
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ),
                  )}
              {provided.placeholder}
            </TableBody>
          )}
        </Droppable>
      </Table>
    </DragDropContext>
  );
};

export default CategoriesTable;
